/**
 * A marca do curso — o que ocupa a caixa quando não há capa.
 *
 * ## O defeito que ela conserta, nos dois lugares de uma vez
 *
 * Um curso sem capa aparecia de duas formas erradas, e a mesma falta explicava
 * as duas:
 *
 * - **Na vitrine**, a caixa vazia recebia o título do curso dentro (16px) e a
 *   legenda repetia o mesmo título embaixo (15px). O mesmo texto duas vezes, a
 *   8px de distância e em tamanhos quase iguais, não identifica nada — só faz
 *   ruído. E dois cursos sem capa saíam com a caixa idêntica: só o texto os
 *   separava.
 * - **Na trilha**, a miniatura da etapa sem capa era um retângulo liso, sem
 *   texto nenhum. Ali é pior: a etapa **é** o marco do trajeto, e um retângulo
 *   cinza no meio do caminho não diz que etapa é aquela.
 *
 * O conserto é um só de propósito. Dois tratamentos do mesmo problema
 * divergiriam na primeira vez que alguém encostasse num deles, e o curso
 * deixaria de ser o mesmo objeto nas duas telas — que é justamente o que a
 * proporção 2:3 compartilhada existe para garantir.
 *
 * ## Por que iniciais, e não o título
 *
 * Porque o título já está escrito ao lado, nas duas telas — na legenda da capa e
 * no corpo do cartão da etapa. O que falta na caixa não é *o nome*: é **algo que
 * distinga uma capa vazia da outra**. Um monograma faz isso e não compete com a
 * legenda, porque ninguém lê "OH" como um título.
 *
 * ## Por que a cor sai do tema, e não de uma paleta nova
 *
 * Porque a área é do cliente: `--destaque` e `--superficie` são escolhas dele. Um
 * matiz derivado do id — o truque usual — plantaria cores que a área nunca
 * declarou, e a vitrine passaria a ter dois sistemas de cor discordando. O que
 * varia aqui é **quanto** do destaque entra na superfície e em que ângulo, e só
 * isso: nenhuma cor que já não seja da área.
 *
 * ## Por que isto é uma função pura, num módulo sem JSX
 *
 * Porque é a parte que pode divergir em silêncio. Duas telas desenhando a mesma
 * caixa com marcas diferentes seria o defeito voltando por outra porta, e é o
 * tipo de coisa que nenhuma captura pega: as duas caixas parecem certas
 * separadas. Aqui a derivação tem trava (`marca-do-curso.test.ts`); o
 * `<Monograma>` que a desenha é dez linhas de JSX sem decisão nenhuma.
 */

/** FNV-1a de 32 bits. Estável entre sessões e entre telas, que é todo o requisito. */
function digitos(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/*
 * As palavras que não carregam identidade.
 *
 * Sem esta lista, "Automações de conversa" viraria "AD" e "Campanhas que fecham
 * a conta" viraria "CQ" — duas siglas que não lembram nada. Com ela, "AC" e "CF".
 */
const VAZIAS = new Set([
  'a', 'as', 'o', 'os', 'de', 'do', 'da', 'dos', 'das', 'e', 'em', 'no', 'na',
  'nos', 'nas', 'para', 'pra', 'por', 'com', 'sem', 'que', 'um', 'uma', 'ao',
]);

/** Até duas letras, das palavras que significam alguma coisa. */
function iniciaisDe(titulo: string): string {
  const palavras = titulo
    .normalize('NFD')
    /* `\p{M}` são as marcas combinantes que o `NFD` acabou de soltar: `Á` vira
       `A`, e o resto do título — inclusive alfabetos não latinos — atravessa
       intacto. Sem isto, `Á` normalizado entra na sigla como acento solto. */
    .replace(/\p{M}/gu, '')
    .split(/[\s\-–—_/]+/)
    .filter(Boolean);

  const uteis = palavras.filter((p) => !VAZIAS.has(p.toLowerCase()));
  const base = uteis.length > 0 ? uteis : palavras;

  if (base.length === 0) return '·';
  if (base.length === 1) return base[0]!.slice(0, 2).toUpperCase();
  return (base[0]![0]! + base[1]![0]!).toUpperCase();
}

/* Quatro pesos e seis ângulos: 24 caixas distinguíveis sem sair das duas cores
 * que a área declarou. O peso máximo é baixo de propósito — a caixa é o fundo de
 * um monograma, não um cartaz; passando disso ela compete com a capa de verdade
 * do cartão ao lado. */
const PESOS = [10, 15, 20, 26];
const ANGULOS = [145, 200, 25, 70, 115, 250];

export interface MarcaDoCurso {
  /** O `background` da caixa. Só `--destaque` e `--superficie`, nas proporções do id. */
  fundo: string;
  /** Até duas letras. Decorativo: o título está escrito ao lado. */
  iniciais: string;
}

export function marcaDoCurso(id: string, titulo: string): MarcaDoCurso {
  const h = digitos(id);
  const peso = PESOS[h % PESOS.length]!;
  const angulo = ANGULOS[(h >>> 5) % ANGULOS.length]!;

  return {
    fundo: `linear-gradient(${angulo}deg, color-mix(in srgb, var(--destaque) ${peso}%, var(--superficie)), var(--superficie))`,
    iniciais: iniciaisDe(titulo),
  };
}
